import { useEffect, useRef, useState } from "react";

import { liveMatchKey } from "../util/liveMatchUtil";

const POLL_INTERVAL_MS = 25 * 1000;

// Normalizes the two match shapes this hook gets called with: the flat
// objects from useAllMatches (homeSlug/awaySlug/homeFullName/awayFullName)
// and a raw MatchesJson page-query node (home.slug/home.fullName), so
// every caller can just pass whichever it already has.
const normalize = (match) => ({
  key: liveMatchKey(match),
  livescoreId: match.livescoreId,
  homeSlug: match.homeSlug || match.home?.slug,
  awaySlug: match.awaySlug || match.away?.slug,
  homeName: match.homeFullName || match.home?.fullName,
  awayName: match.awayFullName || match.away?.fullName,
});

// Polls /api/live-match for each of the given candidate matches, same
// useEffect shape as UpdateBanner.js (immediate fetch, interval,
// visibilitychange re-fetch, per-fetch try/catch so one failure doesn't
// blank the others). Returns a map keyed by liveMatchKey() of each
// match's last known live-match.js response, or {} when there are no
// candidates - the common case, so most pages most of the time this is a
// no-op (no interval ever set up).
export const useLiveMatches = (candidateMatches, { squadNeeded = false } = {}) => {
  const [liveResults, setLiveResults] = useState({});
  const finishedKeysRef = useRef(new Set());

  const candidateKey = candidateMatches.map((m) => m.livescoreId).join(",");

  useEffect(() => {
    const normalized = candidateMatches.map(normalize).filter((m) => m.livescoreId);

    if (normalized.length === 0) {
      return undefined;
    }

    let cancelled = false;

    const poll = async () => {
      const pending = normalized.filter((m) => !finishedKeysRef.current.has(m.key));
      if (pending.length === 0) return;

      const results = await Promise.all(
        pending.map(async (m) => {
          try {
            const params = new URLSearchParams({
              livescoreId: m.livescoreId,
              home: m.homeSlug,
              away: m.awaySlug,
              homeName: m.homeName,
              awayName: m.awayName,
              ...(squadNeeded ? { squadNeeded: "true" } : {}),
            });
            const response = await fetch(`/api/live-match?${params.toString()}`, {
              cache: "no-store",
            });
            if (!response.ok) return null;
            const data = await response.json();
            return { key: m.key, data };
          } catch {
            // Transient/offline failure - keep the previous cached value
            // for this match and just try again next tick.
            return null;
          }
        }),
      );

      if (cancelled) return;

      setLiveResults((prev) => {
        const next = { ...prev };
        for (const result of results) {
          if (!result) continue;
          next[result.key] = result.data;
          if (result.data.status === "FINISHED") {
            finishedKeysRef.current.add(result.key);
          }
        }
        return next;
      });
    };

    poll();
    const intervalId = setInterval(poll, POLL_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        poll();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateKey]);

  return liveResults;
};
